import { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { useLineCalendars, useProductionOrders } from "./hooks";
import { OpTimelineKpis } from "./OpTimelineKpis";
import { TimelineGrid } from "./TimelineGrid";
import { OP_STATUS_BLOCK } from "./production-order-utils";
import { detectConflicts } from "./production-timeline-geometry";
import {
  buildRange,
  periodLabel,
  rangeBoundsIso,
  rangeNowPct,
  shiftAnchor,
  todayIso,
  type TimelineView,
} from "./timeline-range";
import type { ProductionOrder } from "../../types/production-orders.types.js";

const VIEWS: { key: TimelineView; label: string }[] = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "month", label: "Mês" },
];

const LEGEND: { status: ProductionOrder["status"]; label: string }[] = [
  { status: "SCHEDULED", label: "Agendada" },
  { status: "RUNNING", label: "Em execução" },
  { status: "COMPLETED", label: "Concluída" },
  { status: "CANCELLED", label: "Cancelada" },
];

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-text-tertiary">
      {LEGEND.map((it) => {
        const b = OP_STATUS_BLOCK[it.status];
        return (
          <span key={it.status} className="inline-flex items-center gap-1.5">
            <span
              className={cn("h-3 w-3 rounded-sm border border-l-[3px]", b.bg, b.border, b.accent)}
            />
            {it.label}
          </span>
        );
      })}
      <span className="inline-flex items-center gap-1.5">
        <span className="h-3 w-3 rounded-sm border border-dashed border-rd-danger-500 bg-rd-danger-50" />
        Conflito de janela
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="off-hours-hatch bg-bg-muted-rd/30 h-3 w-3 rounded-sm border" />
        Fora do expediente
      </span>
    </div>
  );
}

interface Props {
  onOpenOrder: (o: ProductionOrder) => void;
}

// Visão de planejamento: gantt contínuo com 3 visões (Dia 00–24h / Semana / Mês),
// navegável. Blocos por status (recortados à faixa → OP multi-dia aparece em todo período), marcador
// AGORA, conflitos realçados. Clicar num bloco abre o modal da OP (sem painel lateral).
export function ProductionTimeline({ onOpenOrder }: Props) {
  const [view, setView] = useState<TimelineView>("day");
  const [anchor, setAnchor] = useState(() => todayIso());
  const range = useMemo(() => buildRange(view, anchor), [view, anchor]);
  const { from, to } = useMemo(() => rangeBoundsIso(range), [range]);
  // overlap=true: traz OPs que SOBREPÕEM a faixa, não só as que começam nela (multi-dia em todo dia).
  const timelineQuery = useProductionOrders({ from, to, overlap: true, limit: 200 });
  const { data, isError, error, refetch } = timelineQuery;
  const items = useMemo(() => data?.items ?? [], [data]);
  const conflicts = useMemo(() => detectConflicts(items), [items]);
  // Calendários das linhas visíveis (faixas fora do expediente por swimlane).
  const lineIds = useMemo(() => [...new Set(items.map((o) => o.lineId))], [items]);
  const calendars = useLineCalendars(lineIds);
  // Marcador AGORA vivo: relógio em estado, repassado a cada 30s (sem isto o "AGORA" congela no 1º
  // render). Limpo no unmount. nowPct é null fora da faixa exibida.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const nowPct = rangeNowPct(now, range);
  // Limite duro de 200 OPs por faixa (geralmente só relevante na visão Mês): superfície, nunca esconde.
  // OPs RUNNING entram "fora da janela" e são ISENTAS do teto: `total` (e o "+N") conta só
  // as não-RUNNING, então o hidden compara contra as não-RUNNING exibidas.
  const shownNonRunning = useMemo(
    () => items.filter((o) => o.status !== "RUNNING").length,
    [items]
  );
  const hidden = Math.max((data?.total ?? shownNonRunning) - shownNonRunning, 0);

  if (isError) return <Notify.QueryError error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3">
      <OpTimelineKpis items={items} conflicts={conflicts.size} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border bg-card p-0.5">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className={cn(
                "rounded px-3 py-1 text-sm transition-colors",
                view === v.key && "bg-muted font-medium"
              )}
              onClick={() => setView(v.key)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label="Período anterior"
          onClick={() => setAnchor(shiftAnchor(view, anchor, -1))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <input
          type="date"
          aria-label="Data"
          className="h-9 rounded-md border bg-card px-2 text-sm"
          value={anchor}
          onChange={(e) => e.target.value && setAnchor(e.target.value)}
        />
        <Button
          variant="outline"
          size="icon"
          aria-label="Próximo período"
          onClick={() => setAnchor(shiftAnchor(view, anchor, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setAnchor(todayIso())}>
          <CalendarDays className="mr-1 h-3.5 w-3.5" /> Hoje
        </Button>
        <span className="text-sm capitalize text-text-secondary">{periodLabel(range)}</span>
        <div className="ml-auto">
          <Legend />
        </div>
      </div>

      <TimelineGrid
        items={items}
        range={range}
        conflicts={conflicts}
        nowPct={nowPct}
        now={now}
        onOpenOrder={onOpenOrder}
        calendars={calendars}
      />

      <div className="flex items-center gap-2 text-[11px] text-text-tertiary">
        <Info className="h-3 w-3 shrink-0" />
        Clique numa OP para abrir · Janelas que se encostam (fim ↔ início) são permitidas ·
        Sobreposição é bloqueada no cadastro.
        {hidden > 0 ? (
          <span className="font-medium text-rd-warning-700">
            · +{hidden} OPs além do limite (200) não exibidas.
          </span>
        ) : null}
      </div>
    </div>
  );
}
